import { Button } from '@repo/design-system-cf/components/button'
import {
  IconPause,
  IconPlayArrow,
  IconVolumeOff,
  IconVolumeUp,
} from '@repo/design-system-cf/components/icons/all-icons'
import { Spinner } from '@repo/design-system-cf/components/spinner'

import * as React from 'react'
import { tw } from 'utils/classnames'
import { useResizeObserver } from '@repo/web-ui/hooks/use-resize-observer'

export const ProjectVideo = ({
  src,
  onClick,
  state,
  disablePortraitDetection = true,
}: {
  src: string
  onClick?: (e: React.MouseEvent) => void
  state?: string
  disablePortraitDetection?: boolean
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [containerElement, setContainerElement] = React.useState<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [isMuted, setIsMuted] = React.useState(true)
  const [progress, setProgress] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isPortrait, setIsPortrait] = React.useState(true)
  const [videoState, setVideoState] = React.useState(state)
  const [videoDimensions, setVideoDimensions] = React.useState<{
    width: number
    height: number
  } | null>(null)

  const containerSize = useResizeObserver(containerElement)
  const isMobile = containerSize.width > 0 && containerSize.width < 320
  React.useEffect(() => {
    setVideoState(state)
  }, [state])

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    const updateProgress = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100)
        setCurrentTime(video.currentTime)
      }
    }

    const handleTimeUpdate = () => {
      updateProgress()
    }

    const handleLoadedMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration)
      }

      // Detect video dimensions and orientation
      const videoWidth = video.videoWidth || video.clientWidth
      const videoHeight = video.videoHeight || video.clientHeight

      if (videoWidth > 0 && videoHeight > 0) {
        setVideoDimensions({ width: videoWidth, height: videoHeight })
        setIsPortrait(videoHeight > videoWidth)
      }

      setIsLoading(false)
    }

    const handleLoadedData = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration)
      }

      // Detect video dimensions and orientation (fallback if metadata didn't fire)
      const videoWidth = video.videoWidth || video.clientWidth
      const videoHeight = video.videoHeight || video.clientHeight

      if (videoWidth > 0 && videoHeight > 0 && !videoDimensions) {
        setVideoDimensions({ width: videoWidth, height: videoHeight })
        setIsPortrait(videoHeight > videoWidth)
      }

      setIsLoading(false)
    }

    const handleLoadStart = () => {
      setIsLoading(true)
    }

    const handleCanPlay = () => {
      setIsLoading(false)
    }

    const handleError = () => {
      setIsLoading(false)
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    // Check if duration is already available
    if (video.duration && isFinite(video.duration)) {
      setDuration(video.duration)
      setIsLoading(false)
    }

    // Check if video is already loaded (readyState >= 2 means HAVE_CURRENT_DATA)
    if (video.readyState >= 2) {
      setIsLoading(false)

      // Check dimensions if video is already loaded
      const videoWidth = video.videoWidth || video.clientWidth
      const videoHeight = video.videoHeight || video.clientHeight

      if (videoWidth > 0 && videoHeight > 0) {
        setVideoDimensions({ width: videoWidth, height: videoHeight })
        setIsPortrait(videoHeight > videoWidth)
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Video is visible → set src and play
            if (!video.src) {
              setIsLoading(true)
              video.src = src
              video.load()
            }
            // Autoplay only on desktop (not mobile)
            // oxlint-disable-next-line no-negated-condition
            if (!isMobile) {
              video.play()
            } else {
              // Ensure video is paused on mobile
              video.pause()
            }
          } else {
            // Video is offscreen → stop/unload
            video.pause()
            video.currentTime = 0
            setCurrentTime(0)
            setProgress(0)
            video.removeAttribute('src')
            video.load()
            setDuration(0)
            setVideoDimensions(null)
            setIsPortrait(true) // Reset to default
            setIsLoading(true) // Reset loading state when video is unloaded
          }
        })
      },
      { threshold: 0.2 },
    )

    observer.observe(video)

    return () => {
      observer.unobserve(video)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, isMobile])

  // Pause video when mobile is detected
  React.useEffect(() => {
    const video = videoRef.current
    if (video && isMobile && isPlaying) {
      video.pause()
    }
  }, [isMobile, isPlaying])

  const togglePlayPause = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (isPlaying) {
      video.pause()
    } else {
      // oxlint-disable-next-line no-lonely-if
      if (!isMobile) {
        video.play()
      }
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current
    if (!video || !duration) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    video.currentTime = percentage * duration
  }

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) {
      return '0:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div
      ref={setContainerElement}
      className={tw(
        !isPortrait || disablePortraitDetection
          ? 'w-full aspect-video @container relative overflow-hidden rounded-2 bg-gradient-to-b from-cf-transparent-grey-1000-0 to-cf-transparent-grey-1000-16 group cursor-pointer'
          : 'max-h-[80vh] aspect-[9/16] relative overflow-hidden rounded-2 group cursor-pointer',
      )}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        src={src}
        muted={isMuted}
        playsInline={!isMobile}
        loop={!isMobile}
        autoPlay={!isMobile}
      />
      {/* { debug pangel */}
      {/* <div className="text-cf-white  bg-red-100 w-[100px] absolute flex flex-col items-start justify-start top-0 left-0 p-1 text-[8px]">
        <p>state: {videoState}</p>
        <p>isLoading: {isLoading.toString()}</p>
        <p>isPlaying: {isPlaying.toString()}</p>
        <p>isMuted: {isMuted.toString()}</p>
        <p>progress: {progress.toFixed(2)}</p>
        <p>duration: {duration.toFixed(2)}</p>
        <p>currentTime: {currentTime.toFixed(2)}</p>
        <p>width: {videoDimensions?.width.toString()}</p>
        <p>height: {videoDimensions?.height.toString()}</p>
        <p>isMobile: {isMobile.toString()}</p>
      </div> */}
      {videoState === 'processing' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-cf-grey-50 to-cf-grey-200 z-30">
          <div className="flex flex-col items-center">
            <Spinner variant="default" className="w-8 h-8" />
            <p className="text-cf-grey-1000 @sm:text-16 text-14 font-semibold mt-2">
              We’re still processing this video...
            </p>
            <p className="text-cf-grey-800 @sm:text-14 text-[11px]">Check back later</p>
          </div>
        </div>
      ) : null}
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-cf-grey-1000 z-20">
          <Spinner variant="inverse" className="w-8 h-8" />
        </div>
      ) : null}
      {/* Custom Controls Overlay */}
      {!isMobile ? (
        <div className="absolute inset-0 flex flex-col justify-end transition-opacity duration-200 z-10">
          {onClick ? (
            <div className="absolute top-3 right-3 rounded-2 flex items-center justify-center ">
              <Button
                size="small"
                variant="primary-inverse"
                className="bg-cf-white"
                onClick={e => {
                  e.stopPropagation()
                  onClick?.(e)
                }}
                iconRight={<FullScreenIcon className="text-cf-purple-900 " />}
              />
            </div>
          ) : null}
          <div className="w-full flex bg-gradient-to-b from-cf-transparent-grey-1000-0 to-cf-transparent-grey-1000-16 items-center">
            {/* Control Buttons */}
            <div className="flex items-center gap-2 px-2 pb-2 w-full  ">
              {/* Play/Pause Button */}
              <button
                onClick={e => {
                  e.stopPropagation()
                  togglePlayPause()
                }}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className="transition-colors text-cf-white hover:bg-cf-transparent-grey-0-24 rounded-2 p-1.5"
              >
                {isPlaying ? (
                  <IconPause className="size-6 " />
                ) : (
                  <IconPlayArrow className="size-6 " />
                )}
              </button>
              {/* Time Display */}
              <div className="text-cf-white min-w-[68px] text-nowrap text-sm ml-auto">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
              {/* Progress Bar */}
              <div
                className="w-full h-2 flex-grow pointer-events-none @xs:pointer-events-auto @xs:bg-cf-white cursor-pointer hover:h-2.5 transition-all duration-75 ease-linear bg-transparent"
                onClick={e => {
                  e.stopPropagation()
                  handleProgressClick(e)
                }}
              >
                <div
                  className="h-full hidden @xs:block bg-cf-purple-600 transition-all ease-linear "
                  style={{ width: `${progress}%` }}
                />
              </div>
              {/* Mute/Unmute Button */}
              <button
                onClick={e => {
                  e.stopPropagation()
                  toggleMute()
                }}
                className="transition-colors text-cf-white hover:bg-cf-transparent-grey-0-24 rounded-2 p-1.5"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <IconVolumeUp className="size-6" />
                ) : (
                  <IconVolumeOff className="size-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
          <button
            className="bg-cf-transparent-grey-1000-52 flex items-center justify-center size-9 rounded-2 p-2"
            onClick={e => {
              e.stopPropagation()
              onClick?.(e)
            }}
          >
            <PlayMobileIcon className="text-cf-white size-6" />
          </button>
        </div>
      )}
    </div>
  )
}

const FullScreenIcon = (props: React.ComponentProps<'svg'>) => {
  return (
    <svg
      {...props}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.75 14.25V9.75H5.25V12.75H8.25V14.25H3.75ZM12.75 8.25V5.25H9.75V3.75H14.25V8.25H12.75Z"
        fill="currentColor"
      />
    </svg>
  )
}

const PlayMobileIcon = (props: React.ComponentProps<'svg'>) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 14.5V5.5L14 10L8 14.5Z"
        fill="currentColor"
      />
    </svg>
  )
}
